<template>
	<a-modal
		class="g-audio-player-edit-modal"
		:visible="visible"
		title="模块编辑"
		width="730px"
		:centered="true"
		:mask-closable="false"
		:destroy-on-close="true"
		@cancel="onCancel"
	>
		<div class="g-edit-modal__scroll">
		<div class="g-audio-player-edit">
			<div
				class="g-audio-player-edit__preview"
				:style="{ width: `${form.playerWidth}%` }"
			>
				<div
					class="g-audio-player-edit__shell"
					:style="{ borderRadius: `${form.borderRadius}px` }"
				>
					<UploadProgressPanel
						v-if="isUploading"
						:tasks="uploadTasks"
						variant="item"
						hide-filename
					/>
					<template v-else>
					<img
						v-if="bgUrl"
						class="g-audio-player-edit__bg"
						:src="bgUrl"
						alt=""
					/>
					<div
						v-if="form.darkOverlay"
						class="g-audio-player-edit__overlay"
					></div>
					<div class="g-audio-player-edit__content">
						<div class="g-audio-player-edit__meta">
							<input
								v-model="form.title"
								type="text"
								class="g-audio-player-edit__title-input"
								:style="{ color: form.titleColor }"
								maxlength="40"
								placeholder="歌曲名称"
							/>
							<input
								v-model="form.subtitle"
								type="text"
								class="g-audio-player-edit__subtitle-input"
								:style="{ color: form.subtitleColor }"
								maxlength="40"
								placeholder="副标题"
							/>
						</div>
						<div class="g-audio-player-edit__progress-wrap">
							<div
								ref="previewProgressTrack"
								class="g-audio-player-edit__progress-track"
								:class="{ 'is-interactive': canPreviewPlay }"
								:style="trackStyle"
								@click="onPreviewSeek"
							>
								<div
									class="g-audio-player-edit__progress-bar"
									:style="progressBarStyle"
								></div>
								<div
									class="g-audio-player-edit__progress-thumb"
									:style="thumbStyle"
								></div>
							</div>
							<div class="g-audio-player-edit__time" :style="timeStyle">
								<span>{{ previewCurrentTimeText }}</span>
								<span>{{ previewDurationText }}</span>
							</div>
						</div>
						<div class="g-audio-player-edit__controls">
							<button
								type="button"
								class="g-audio-player-edit__play"
								:style="iconStyle"
								:disabled="!canPreviewPlay"
								:aria-label="previewIsPlaying ? '暂停' : '播放'"
								@click="togglePreviewPlay"
							>
								<span v-if="previewIsPlaying" class="g-audio-player-edit__pause"></span>
								<span v-else class="g-audio-player-edit__triangle"></span>
							</button>
						</div>
					</div>
					<audio
						v-if="previewAudioUrl"
						ref="previewAudioEl"
						:src="previewAudioUrl"
						preload="metadata"
						@loadedmetadata="onPreviewLoadedMetadata"
						@timeupdate="onPreviewTimeUpdate"
						@ended="onPreviewEnded"
						@pause="onPreviewPause"
						@play="onPreviewPlay"
					></audio>
					</template>
				</div>
			</div>

			<div class="g-audio-player-edit__panel">
				<div class="g-audio-player-edit__toolbar">
					<button type="button" class="g-audio-player-edit__action" @click="triggerAudioSelect">换音乐</button>
					<button type="button" class="g-audio-player-edit__action" @click="triggerBgSelect">换图</button>
					<button
						type="button"
						class="g-audio-player-edit__action"
						:class="{ 'is-active': form.darkOverlay }"
						@click="toggleDarkOverlay"
					>底图变暗</button>
					<button
						type="button"
						class="g-audio-player-edit__action"
						:class="{ 'is-active': activePanel === 'radius' }"
						@click="togglePanel('radius')"
					>圆角</button>
					<button
						type="button"
						class="g-audio-player-edit__action"
						:class="{ 'is-active': activePanel === 'width' }"
						@click="togglePanel('width')"
					>宽度</button>
					<div class="g-audio-player-edit__colors">
						<ColorPicker
							v-model="form.titleColor"
							title="标题颜色"
							@change="onTitleColorInput"
						/>
						<ColorPicker
							v-model="form.subtitleColor"
							title="副标题颜色"
							@change="onSubtitleColorInput"
						/>
						<ColorPicker
							v-model="form.themeColor"
							title="主题色（进度条/按钮）"
							@change="onThemeColorInput"
						/>
					</div>
				</div>
				<div v-if="activePanel === 'radius'" class="g-audio-player-edit__sub-row">
					<span class="g-audio-player-edit__sub-label">圆角 (px)</span>
					<a-input-number
						v-model:value="radiusDraft"
						:min="0"
						:max="100"
						:precision="0"
						class="g-audio-player-edit__sub-input"
					/>
					<button type="button" class="g-audio-player-edit__sub-confirm" @click="applyRadius">确定</button>
				</div>
				<div v-if="activePanel === 'width'" class="g-audio-player-edit__sub-row">
					<span class="g-audio-player-edit__sub-label">宽度 (%)</span>
					<a-input-number
						v-model:value="widthDraft"
						:min="20"
						:max="100"
						:precision="0"
						class="g-audio-player-edit__sub-input"
					/>
					<button type="button" class="g-audio-player-edit__sub-confirm" @click="applyWidth">确定</button>
				</div>
			</div>
		</div>
		</div>

		<input
			ref="bgInput"
			type="file"
			accept="image/*"
			class="g-audio-player-edit__file-input"
			@change="onBgSelect"
		/>
		<input
			ref="audioInput"
			type="file"
			accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac,.wma"
			class="g-audio-player-edit__file-input"
			@change="onAudioSelect"
		/>

		<template #footer>
			<a-button @click="onCancel">取消</a-button>
			<a-button type="primary" class="g-audio-player-edit-modal__ok" :disabled="isUploading" @click="onConfirm">确定</a-button>
		</template>
	</a-modal>
</template>

<script>
	import { getSwatchBorderColor, normalizeHexColor } from '../utils/richText.js';
	import ColorPicker from './ColorPicker.vue';
	import {
		isLocalImageFile,
		MAX_LOCAL_IMAGE_SIZE,
		revokeLocalImageUrl,
	} from '../utils/localImage.js';
	import {
		filterValidLocalAudioList,
		isAudioFilename,
		MAX_LOCAL_AUDIO_SIZE,
	} from '../utils/localAudio.js';
	import {
		AUDIO_PLAYER_DEFAULT_BORDER_RADIUS,
		AUDIO_PLAYER_DEFAULT_WIDTH,
		getAudioPlayerAudioUrl,
		getAudioPlayerCoverUrl,
		normalizeAudioPlayerData,
		replaceAudioPlayerAudio,
		replaceAudioPlayerCover,
	} from '../utils/audioPlayer.js';
	import editModalAudioPreview from '../mixins/editModalAudioPreview.js';
	import UploadProgressPanel from './UploadProgressPanel.vue';
	import {
		createUploadTasks,
		updateUploadTaskProgress,
		uploadHeaderMediaFiles,
	} from '../utils/uploadHeaderMedia.js';

	export default {
		name: 'GAudioPlayerEditModal',
		components: { UploadProgressPanel, ColorPicker },
		mixins: [editModalAudioPreview],
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			data: {
				type: Object,
				default: () => ({}),
			},
		},
		emits: ['update:visible', 'confirm'],
		data() {
			return {
				form: normalizeAudioPlayerData({}),
				activePanel: '',
				radiusDraft: AUDIO_PLAYER_DEFAULT_BORDER_RADIUS,
				widthDraft: AUDIO_PLAYER_DEFAULT_WIDTH,
				isUploading: false,
				uploadTasks: [],
			};
		},
		computed: {
			bgUrl() {
				return getAudioPlayerCoverUrl(this.form);
			},
			previewAudioUrl() {
				return getAudioPlayerAudioUrl(this.form);
			},
			previewAudioFallbackDuration() {
				return this.form.audio?.duration || 0;
			},
			trackStyle() {
				return {
					background: this.hexToRgba(this.form.themeColor, 0.35),
				};
			},
			progressBarStyle() {
				return {
					width: `${this.previewProgressPercent}%`,
					background: this.form.themeColor,
				};
			},
			thumbStyle() {
				return {
					left: `${this.previewProgressPercent}%`,
					background: this.form.themeColor,
				};
			},
			timeStyle() {
				return { color: this.form.themeColor };
			},
			iconStyle() {
				return { color: this.form.themeColor };
			},
		},
		watch: {
			visible(val) {
				if (val) {
					this.activePanel = '';
					this.syncForm();
					this.resetPreviewPlayer();
				} else {
					this.pausePreviewAudio();
				}
			},
			data: {
				deep: true,
				handler() {
					if (this.visible) {
						this.syncForm();
					}
				},
			},
		},
		methods: {
			normalizeColor(color) {
				return normalizeHexColor(color);
			},
			hexToRgba(hex, alpha) {
				const normalized = normalizeHexColor(hex).replace('#', '');
				const full = normalized.length === 3
					? normalized.split('').map(c => c + c).join('')
					: normalized;
				const num = parseInt(full, 16);
				if (Number.isNaN(num)) return `rgba(232, 76, 61, ${alpha})`;
				const r = (num >> 16) & 255;
				const g = (num >> 8) & 255;
				const b = num & 255;
				return `rgba(${r}, ${g}, ${b}, ${alpha})`;
			},
			getSwatchStyle(color) {
				const normalized = normalizeHexColor(color);
				return {
					background: normalized,
					borderColor: getSwatchBorderColor(normalized),
				};
			},
			syncForm() {
				this.form = normalizeAudioPlayerData(this.data);
				this.radiusDraft = this.form.borderRadius;
				this.widthDraft = this.form.playerWidth;
			},
			togglePanel(panel) {
				this.activePanel = this.activePanel === panel ? '' : panel;
				if (panel === 'radius') {
					this.radiusDraft = this.form.borderRadius;
				}
				if (panel === 'width') {
					this.widthDraft = this.form.playerWidth;
				}
			},
			toggleDarkOverlay() {
				this.form.darkOverlay = !this.form.darkOverlay;
			},
			triggerBgSelect() {
				this.$refs.bgInput?.click();
			},
			triggerAudioSelect() {
				this.$refs.audioInput?.click();
			},
			async onBgSelect(event) {
				const file = event.target.files?.[0];
				event.target.value = '';
				if (!file || !isLocalImageFile(file)) return;
				if (file.size > MAX_LOCAL_IMAGE_SIZE) return;
				this.isUploading = true;
				this.uploadTasks = createUploadTasks([file]);
				try {
					const [item] = await uploadHeaderMediaFiles(this, {
						type: 'image',
						files: [file],
						onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					if (this.form.cover?.local) {
						revokeLocalImageUrl(this.form.cover.previewUrl);
					}
					this.form.cover = replaceAudioPlayerCover(this.form.cover, item);
				} catch (error) {
					this.$message?.error?.('上传失败');
				} finally {
					this.isUploading = false;
					this.uploadTasks = [];
				}
			},
			async onAudioSelect(event) {
				const file = event.target.files?.[0];
				event.target.value = '';
				if (!file) return;
				if (!file.type?.startsWith('audio/') && !isAudioFilename(file.name)) return;
				if (file.size > MAX_LOCAL_AUDIO_SIZE) return;
				const [validFile] = filterValidLocalAudioList([file], 1);
				if (!validFile) return;
				this.isUploading = true;
				this.uploadTasks = createUploadTasks([validFile]);
				try {
					const [item] = await uploadHeaderMediaFiles(this, {
						type: 'audio',
						files: [validFile],
						onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					this.form.audio = replaceAudioPlayerAudio(this.form.audio, item);
				} catch (error) {
					this.$message?.error?.('上传失败');
				} finally {
					this.isUploading = false;
					this.uploadTasks = [];
				}
			},
			onTitleColorInput(color) {
				this.form.titleColor = color;
			},
			onSubtitleColorInput(color) {
				this.form.subtitleColor = color;
			},
			onThemeColorInput(color) {
				this.form.themeColor = color;
			},
			applyRadius() {
				this.form.borderRadius = Math.max(0, Number(this.radiusDraft) || 0);
				this.activePanel = '';
			},
			applyWidth() {
				const width = Number(this.widthDraft) || AUDIO_PLAYER_DEFAULT_WIDTH;
				this.form.playerWidth = Math.min(100, Math.max(20, width));
				this.activePanel = '';
			},
			onCancel() {
				this.$emit('update:visible', false);
			},
			onConfirm() {
				const payload = normalizeAudioPlayerData({ ...this.form });
				this.$emit('confirm', payload);
				this.$emit('update:visible', false);
			},
		},
	};
</script>

<style scoped lang="less">
	@import '../styles/edit-float-panel.less';

	.edit-float-panel(g-audio-player-edit__panel);
	.edit-float-toolbar(g-audio-player-edit__toolbar);
	.edit-float-action(g-audio-player-edit__action);

	.g-audio-player-edit {
		padding: 4px 0 12px;
	}

	.g-audio-player-edit__preview {
		margin: 0 auto 16px;
		max-width: 100%;
	}

	.g-audio-player-edit__shell {
		position: relative;
		width: 100%;
		aspect-ratio: 2.35 / 1;
		overflow: hidden;
		background: #1a1a2e;
	}

	.g-audio-player-edit__bg {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}

	.g-audio-player-edit__overlay {
		position: absolute;
		inset: 0;
		background: rgba(0, 0, 0, 0.35);
		pointer-events: none;
	}

	.g-audio-player-edit__content {
		position: relative;
		z-index: 1;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		height: 100%;
		padding: 14px 16px 12px;
		box-sizing: border-box;
	}

	.g-audio-player-edit__title-input,
	.g-audio-player-edit__subtitle-input {
		display: block;
		width: 100%;
		border: none;
		background: transparent;
		outline: none;
		padding: 0;
		box-sizing: border-box;
	}

	.g-audio-player-edit__title-input {
		font-size: 16px;
		font-weight: 700;
		line-height: 1.35;
	}

	.g-audio-player-edit__subtitle-input {
		margin-top: 10px;
		font-size: 12px;
		line-height: 1.4;
	}

	.g-audio-player-edit__progress-wrap {
		margin-top: auto;
		padding-top: 8px;
	}

	.g-audio-player-edit__progress-track {
		position: relative;
		height: 3px;
		border-radius: 2px;

		&.is-interactive {
			cursor: pointer;
		}
	}

	.g-audio-player-edit__progress-bar {
		position: absolute;
		left: 0;
		top: 0;
		height: 100%;
		border-radius: inherit;
		pointer-events: none;
	}

	.g-audio-player-edit__progress-thumb {
		position: absolute;
		top: 50%;
		width: 10px;
		height: 10px;
		margin: -5px 0 0 -5px;
		border-radius: 50%;
		pointer-events: none;
	}

	.g-audio-player-edit__time {
		display: flex;
		justify-content: space-between;
		margin-top: 6px;
		font-size: 11px;
		line-height: 1;
	}

	.g-audio-player-edit__controls {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 28px;
		margin-top: 4px;
	}

	.g-audio-player-edit__play {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0;
		border: none;
		background: transparent;
		color: inherit;
		cursor: pointer;
		line-height: 1;

		&:disabled {
			cursor: not-allowed;
			opacity: 0.45;
		}
	}

	.g-audio-player-edit__triangle {
		display: block;
		width: 0;
		height: 0;
		margin-left: 4px;
		border-top: 10px solid transparent;
		border-bottom: 10px solid transparent;
		border-left: 16px solid currentColor;
	}

	.g-audio-player-edit__pause {
		position: relative;
		display: block;
		width: 14px;
		height: 16px;

		&::before,
		&::after {
			content: '';
			position: absolute;
			top: 0;
			width: 4px;
			height: 16px;
			border-radius: 1px;
			background: currentColor;
		}

		&::before {
			left: 0;
		}

		&::after {
			right: 0;
		}
	}

	.g-audio-player-edit__colors {
		display: flex;
		align-items: center;
		gap: 6px;
		margin-left: auto;
		flex-shrink: 0;
	}

	.g-audio-player-edit__swatch {
		position: relative;
		width: 18px;
		height: 18px;
		border: 1px solid #d9d9d9;
		border-radius: 2px;
		cursor: pointer;
		flex-shrink: 0;
		overflow: hidden;
	}

	.g-audio-player-edit__color-input {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		opacity: 0;
		cursor: pointer;
		border: none;
		padding: 0;
	}

	.g-audio-player-edit__sub-row {
		display: flex;
		align-items: center;
		flex-wrap: nowrap;
		gap: 8px;
		padding: 8px 12px 10px;
		border-top: 1px solid #f0f0f0;
		white-space: nowrap;
	}

	.g-audio-player-edit__sub-label {
		font-size: 13px;
		color: #666;
		flex-shrink: 0;
	}

	.g-audio-player-edit__sub-input {
		width: 88px;
		flex-shrink: 0;
	}

	.g-audio-player-edit__sub-confirm {
		flex-shrink: 0;
		height: 28px;
		padding: 0 12px;
		border: none;
		border-radius: 4px;
		color: #fff;
		background: #285af9;
		font-size: 12px;
		line-height: 28px;
		cursor: pointer;

		&:hover {
			background: #3868ff;
		}
	}

	.g-audio-player-edit__file-input {
		display: none;
	}
</style>

<style lang="less">
	@import '../styles/edit-float-panel.less';

	.g-audio-player-edit-modal {
		.edit-modal-body();
		.edit-modal-scroll();

		.g-audio-player-edit-modal__ok {

			&:hover,
			&:focus {
				background: #3868ff;
				border-color: #3868ff;
			}
		}
	}
</style>
